/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React from 'react';
import Box from '@atlaskit/primitives/box';
import Stack from '@atlaskit/primitives/stack';
import { xcss } from '@atlaskit/primitives/xcss';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { lazyForPaint } from '@atlassian/react-loosely-lazy';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import type { PageName } from './types.tsx';

type AsyncPageSwitcherProps = {
	currentPageName: PageName;
};

export const Skeleton = (): React.JSX.Element => (
	<Stack space="space.100" grow="hug">
		<Box xcss={headingSkeleton} />
		<Box xcss={dropdownSkeleton} />
	</Stack>
);

const LazyPageSwitcher = lazyForPaint(
	() =>
		import(
			/* webpackChunkName: "async-jira-navigation-apps-sidebar-nav4-sidebar-global-settings-page-switcher" */ './main.tsx'
		),
	{
		includeInShellOnlySsrBundle: true,
		entryFsSsrLiftImportToModule: true,
	},
);

const LazyPageSwitcherWithQuery = lazyForPaint(
	() =>
		import(
			/* webpackChunkName: "async-jira-navigation-apps-sidebar-nav4-sidebar-global-settings-page-switcher-with-query" */ './PageSwitcherWithQuery.tsx'
		),
	{
		includeInShellOnlySsrBundle: true,
		entryFsSsrLiftImportToModule: true,
	},
);

export const AsyncPageSwitcher = ({
	currentPageName,
}: AsyncPageSwitcherProps): React.JSX.Element => {
	const isAdmin = useIsAdmin();

	return (
		<Placeholder name="nav4.sidebar.global-settings.page-switcher" fallback={<Skeleton />}>
			{!isAdmin && fg('enable_jsm_admin') ? (
				<LazyPageSwitcherWithQuery currentPageName={currentPageName} />
			) : (
				<LazyPageSwitcher currentPageName={currentPageName} />
			)}
		</Placeholder>
	);
};

const headingSkeleton = xcss({
	backgroundColor: 'color.skeleton',
	width: '50%',
	height: token('space.200'),
	borderRadius: 'radius.small',
});

const dropdownSkeleton = xcss({
	backgroundColor: 'color.skeleton',
	height: token('space.400'),
	borderRadius: 'radius.small',
});
